import { Outlet } from "@tanstack/react-router";
import { supplierIdLayoutRoute } from "router/routes/suppliers";

import Button from "components/ui/button/Button";
import EmptyState from "components/ui/empty_state/EmptyState";
import Spinner from "components/ui/spinner/Spinner";
import { SupplierFromQuery, useSupplierLookup } from "lib/hooks/api/supplier/useSupplierDetails";
import { useConfirmUserDataSubscription } from "lib/providers/Paywall";

/** Access the supplier loaded by the parent layout; cache hits dedupe the query. */
export function useSupplierFromRoute(): SupplierFromQuery | null {
  const { id } = supplierIdLayoutRoute.useParams();
  const { data } = useSupplierLookup(id);
  return data;
}

/**
 * Renders the supplier-detail subtree. The route loader resolves the supplier and issues
 * any numeric→GUID / archived-replacement redirects up-front, so this component only handles
 * the loading, error, and render-children states.
 */
export function SupplierDetailLayout() {
  const { id } = supplierIdLayoutRoute.useParams();

  // Paywall fires once per supplier mount across all child routes.
  useConfirmUserDataSubscription("SUPPLIERS", "SUPPLIERS");

  const { data: supplier, isLoading } = useSupplierLookup(id);

  if (isLoading) {
    return <Spinner label="Loading supplier" />;
  }

  if (!supplier) {
    return (
      <EmptyState
        type="loadError"
        size="large"
        width="100%"
        yAxisMargin="auto"
        heading="Unable to load supplier details"
        description="We couldn't load the supplier information. This could be due to a network error or the supplier data being temporarily unavailable."
        action={
          <Button buttonType="primary" onClick={() => window.location.reload()}>
            Refresh page
          </Button>
        }
      />
    );
  }

  return <Outlet />;
}
